import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { History, Bike, Package, ShoppingCart, MapPin } from 'lucide-react';
import { useAuth } from '../../USER/Auth/AuthContext';
import { getUserServices, getDriverServices, getDriverProfile } from '../services/deliveryServices';
import { SERVICE_STATUSES, SERVICE_TYPES, ROLES } from '../../../constants';
import BackButton from '../../UI/BackButton';
import { BrySkeleton, BryEmptyState } from '../../UI/BryUI';

function ServiceHistory() {
  const { currentUser } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDriver, setIsDriver] = useState(false);
  const [viewMode, setViewMode] = useState('client'); // client | driver

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;
      try {
        const driverProfile = await getDriverProfile(currentUser.id);
        if (driverProfile) {
          setIsDriver(true);
        }

        let data;
        if (viewMode === ROLES.DRIVER && driverProfile) {
          data = await getDriverServices(currentUser.id);
        } else {
          data = await getUserServices(currentUser.id);
        }
        setServices(data);
      } catch (error) {
        console.error('Error al cargar historial:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentUser, viewMode]);

  const isDriverView = viewMode === ROLES.DRIVER;

  const theme = {
    bg: isDriverView ? "bg-black text-gray-100 min-h-screen pb-20" : "bg-gray-50 text-gray-900 min-h-screen pb-20",
    card: isDriverView ? "bg-gray-900 shadow-xl rounded-2xl border border-gray-800" : "bg-white shadow-sm rounded-2xl border border-gray-100",
    textPrimary: isDriverView ? "text-white" : "text-gray-900",
    textMuted: isDriverView ? "text-gray-400" : "text-gray-500",
    iconBgPrimary: isDriverView ? "bg-gray-800" : "bg-cyan-50",
    iconColorPrimary: "text-[var(--bry-accent)]",
    divider: isDriverView ? "border-gray-800" : "border-gray-100",
    addressCard: isDriverView ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200",
    btnClient: viewMode === 'client' 
      ? (isDriverView ? "bg-[var(--bry-accent)] text-black" : "bg-[var(--bry-accent)] text-white") 
      : (isDriverView ? "border border-[var(--bry-accent)] text-[var(--bry-accent)]" : "bg-gray-100 text-gray-700"),
    btnDriver: viewMode === ROLES.DRIVER 
      ? (isDriverView ? "bg-[var(--bry-accent)] text-black" : "bg-[var(--bry-accent)] text-white") 
      : (isDriverView ? "border border-[var(--bry-accent)] text-[var(--bry-accent)]" : "bg-gray-100 text-gray-700"),
    statCard1: isDriverView ? "bg-[var(--bry-accent)]/10 border-[var(--bry-accent)]/30" : "bg-emerald-50 border-emerald-100",
    statCard2: isDriverView ? "bg-green-900/20 border-green-900" : "bg-green-50 border-green-100",
    statCard3: isDriverView ? "bg-[var(--bry-accent)]/20 border-[var(--bry-accent)]/40" : "bg-teal-50 border-teal-100",
  };

  const getStatusBadge = (status) => {
    const map = {
      searching: { bg: isDriverView ? 'bg-yellow-900/30 text-yellow-500 border border-yellow-900' : 'bg-yellow-100 text-yellow-700', text: 'Buscando...' },
      driver_assigned: { bg: isDriverView ? 'bg-[var(--bry-accent)]/20 text-[var(--bry-accent)] border border-[var(--bry-accent)]/40' : 'bg-emerald-100 text-emerald-800', text: 'Asignado' },
      in_progress: { bg: isDriverView ? 'bg-cyan-900/30 text-cyan-500 border border-cyan-900' : 'bg-cyan-100 text-cyan-700', text: 'En curso' },
      completed: { bg: isDriverView ? 'bg-green-900/30 text-green-500 border border-green-900' : 'bg-green-100 text-green-700', text: 'Completado' },
      cancelled: { bg: isDriverView ? 'bg-gray-800 text-gray-400 border border-gray-700' : 'bg-gray-200 text-gray-700', text: 'Cancelado' },
      no_drivers_available: { bg: isDriverView ? 'bg-gray-800 text-gray-400 border border-gray-700' : 'bg-gray-200 text-gray-700', text: 'Sin chofer' },
    };
    const s = map[status] || { bg: isDriverView ? 'bg-gray-800 text-gray-400' : 'bg-gray-200 text-gray-700', text: status };
    return <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${s.bg}`}>{s.text}</span>;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case SERVICE_TYPES.RIDE: return <Bike size={18} className={theme.iconColorPrimary} />;
      case SERVICE_TYPES.DELIVERY: return <Package size={18} className="text-orange-500" />;
      case SERVICE_TYPES.ERRAND: return <ShoppingCart size={18} className="text-purple-500" />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className={theme.bg}>
        <div className="px-4 py-6">
          <div className="mb-4">
             <BrySkeleton type="custom" width="40px" height="40px" className="rounded-full" />
          </div>
          <div className="flex flex-wrap items-center justify-between mb-6 gap-3">
             <div className="flex items-center gap-2 w-1/2">
                <BrySkeleton type="custom" width="24px" height="24px" className="rounded-full" />
                <BrySkeleton type="title" width="100%" className="!mb-0" />
             </div>
             {isDriverView && (
               <div className="flex gap-2">
                 <BrySkeleton type="custom" width="90px" height="32px" className="rounded-lg" />
                 <BrySkeleton type="custom" width="90px" height="32px" className="rounded-lg" />
               </div>
             )}
          </div>
          <div className="flex flex-col gap-4 mt-4">
             {[1, 2, 3].map(i => (
                <div key={i} className={`${theme.card} p-4`}>
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                       <BrySkeleton type="avatar" className="w-11 h-11" />
                       <div>
                         <BrySkeleton type="text" width="80px" className="mb-1" />
                         <BrySkeleton type="text" width="120px" />
                       </div>
                    </div>
                    <div className="text-right">
                       <BrySkeleton type="title" width="60px" className="mb-1 ml-auto" />
                       <BrySkeleton type="custom" width="70px" height="20px" className="rounded-full ml-auto" />
                    </div>
                  </div>
                  <div className={`rounded-xl p-3 border ${theme.addressCard}`}>
                     <BrySkeleton type="text" width="40px" className="mb-1" />
                     <BrySkeleton type="text" width="80%" className="mb-3" />
                     <BrySkeleton type="text" width="40px" className="mb-1" />
                     <BrySkeleton type="text" width="80%" />
                  </div>
                </div>
             ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={theme.bg}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="px-4 py-6"
      >
        <div className="mb-4">
          <BackButton />
        </div>
        
        <div className="flex flex-wrap items-center justify-between mb-6 gap-3">
          <h3 className={`text-xl font-bold flex items-center m-0 ${theme.textPrimary}`}>
            <History className="mr-2" size={24} />
            Historial de Servicios
          </h3>
          {isDriver && (
            <div className="flex gap-2">
              <button
                className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-all ${theme.btnClient}`}
                onClick={() => setViewMode('client')}
              >
                Como Cliente
              </button>
              <button
                className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-all ${theme.btnDriver}`}
                onClick={() => setViewMode(ROLES.DRIVER)}
              >
                Como Conductor
              </button>
            </div>
          )}
        </div>

        {services.length === 0 ? (
          <BryEmptyState
            icon={Bike}
            title="Sin actividad aún"
            description="Tus viajes y entregas aparecerán aquí una vez que comiences a usar el servicio."
            className={`${theme.card} mt-4`}
            iconClassName={`${theme.iconBgPrimary} ${theme.iconColorPrimary}`}
            titleClassName={theme.textPrimary}
            descriptionClassName={theme.textMuted}
          />
        ) : (
          <div className="flex flex-col gap-4 mt-4">
            {services.map((service) => (
              <div 
                key={service.service_id} 
                className={`${theme.card} transition-transform hover:-translate-y-0.5`}
              >
                <div className="p-4">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-11 h-11 rounded-full flex justify-center items-center shadow-sm shrink-0 ${theme.iconBgPrimary}`}>
                        {getTypeIcon(service.type)}
                      </div>
                      <div>
                        <span className={`font-bold block text-sm ${theme.textPrimary}`}>
                          {service.type === SERVICE_TYPES.DELIVERY ? 'Paquetería' : 'Traslado'}
                        </span>
                        <span className={`font-semibold block text-[11px] ${theme.textMuted}`}>
                          {new Date(service.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })} • {new Date(service.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-bold block text-lg text-[var(--bry-accent)] mb-1">
                        $ {service.price?.toFixed(2) || '0.00'}
                      </span>
                      {getStatusBadge(service.status)}
                    </div>
                  </div>

                  <div className={`rounded-xl p-3 relative border ${theme.addressCard}`}>
                    <div className={`absolute left-[22px] top-[30px] w-0.5 h-[calc(100%-60px)] ${isDriverView ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
                    
                    <div className="flex items-start mb-3 relative">
                      <div className={`mt-1 mr-3 z-10 w-2 h-2 rounded-full bg-[var(--bry-accent)] ring-4 ${isDriverView ? 'ring-gray-800' : 'ring-gray-50'} shrink-0 ml-1.5`}></div>
                      <div>
                        <span className={`block text-[10px] font-bold tracking-wider ${theme.textMuted}`}>ORIGEN</span>
                        <span className={`block text-xs font-bold line-clamp-1 ${theme.textPrimary}`}>
                          {service.pickup?.address || 'N/A'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-start relative">
                      <div className={`mt-1 mr-3 z-10 text-pink-500 ring-4 ${isDriverView ? 'ring-gray-800 bg-gray-800' : 'ring-gray-50 bg-gray-50'} shrink-0 ml-0.5`}>
                        <MapPin size={12} />
                      </div>
                      <div>
                        <span className={`block text-[10px] font-bold tracking-wider ${theme.textMuted}`}>DESTINO</span>
                        <span className={`block text-xs font-bold line-clamp-1 ${theme.textPrimary}`}>
                          {service.dropoff?.address || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Resumen */}
        {services.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6 mb-6">
            <div className={`rounded-2xl p-4 text-center flex flex-col justify-center border ${theme.statCard1}`}>
              <span className={`text-[11px] font-bold tracking-wider mb-1 ${isDriverView ? 'text-[var(--bry-accent)]' : 'text-emerald-700'}`}>TOTAL VIAJES</span>
              <h3 className={`text-2xl font-bold m-0 ${isDriverView ? 'text-emerald-300' : 'text-emerald-900'}`}>{services.length}</h3>
            </div>
            
            <div className={`rounded-2xl p-4 text-center flex flex-col justify-center border ${theme.statCard2}`}>
              <span className={`text-[11px] font-bold tracking-wider mb-1 ${isDriverView ? 'text-green-400' : 'text-green-600'}`}>COMPLETADOS</span>
              <h3 className={`text-2xl font-bold m-0 ${isDriverView ? 'text-green-300' : 'text-green-800'}`}>{services.filter(s => s.status === SERVICE_STATUSES.COMPLETED).length}</h3>
            </div>
            
            <div className={`rounded-2xl p-4 text-center flex flex-col justify-center border col-span-2 md:col-span-1 ${theme.statCard3}`}>
              <span className={`text-[11px] font-bold tracking-wider mb-1 ${isDriverView ? 'text-cyan-400' : 'text-cyan-700'}`}>{isDriverView ? 'INGRESOS TOTALES' : 'GASTO TOTAL'}</span>
              <h3 className="text-2xl font-bold m-0 text-[var(--bry-accent)]">$ {services.filter(s => s.status === SERVICE_STATUSES.COMPLETED).reduce((sum, s) => sum + (s.price || 0), 0).toFixed(2)}</h3>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default ServiceHistory;
